import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import { useAPICall } from '../../../../hooks';
import userService from '../../../../sdk/services/userService';
import {
  Accordion,
  AdminPageHeader,
  ButtonStyles,
  DropDown,
  DropDownValidations,
  FrameworkRole,
  Gender,
  Input,
  InputTypes,
  InputValidations,
  MLPVersion,
  AdminType,
  MemberType,
  PhoneNumber,
  RadioButton,
  Status,
  TableContainer,
  Theme,
} from '../../../../ui/components';
import { LoggerService } from '../../../../ui/utils';
import { DropDownData } from '../../../../ui/components/Form/DropDown/types/propsType';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { countryCodeNumber } from '../../../../__mockData/phoneNumber';
import { formatCampaignId, isValidEmail } from '../../../../ui/utils/common';
import { states } from '../../../../__mockData/states';
import ParticipantService from '../../../../sdk/services/participantService';
import { getParticipationHistoryConfig } from '../../../profile/profileConfig';
import { ControlIconType, FontSize } from '../../../../ui/components/Accordion/Accordion';
import ShareDrawer from '../../../../ui/components/DrawerAlt/ShareDrawer';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import { REACT_APP_COUNTRY } from '../../../../envVars';
import styles from './index.module.scss';

interface UserData {
  userId: string;
  email: string;
  phone: string;
  loginId: string;
  partyId: string;
  frameworkRole: string;
  memberType: string;
  adminType: string;
  isPrimary: string;
  name: string;
  gender: string;
  status: string;
  address: string;
  state: string;
  race: string;
  occupation: string;
  incomeRange: string;
  lastLogin: string;
  isAcknowledged: number;

  //[key: string]: string | undefined;
}

export const AdminUserEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminToast } = useAdminToast();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('');
  const [userData, setUserData] = useState<UserData | null>(null);
  const [gender, setGender] = useState('');
  const [status, setStatus] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [invalid, setInvalid] = useState(true);
  const [phoneClicked, setPhoneClicked] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [clickedFields, setClickedFields] = useState({
    loginId: false,
    partyId: false,
    name: false,
    frameworkRole: false,
    memberType: false,
    email: false,
    phone: false,
    address: false,
    state: false,
    race: false,
    occupation: false,
    incomeRange: false,
    lastLogin: false,
    isAcknowledged: false,
  });
  const [participationHistory, setParticipationHistory] = useState([]);
  const [selectedData, setSelectedData] = useState({
    campaignId: '',
    teamId: '',
    code: '',
  });

  const [dropdownValue, setDropdownValue] = useState('');
  const isNewUser = id === 'new';
  const apiCall = useAPICall();
  const adminTypeOptions = [
    { title: 'SUPERADMIN', value: AdminType.SUPERADMIN },
    { title: 'CHALLENGE', value: AdminType.CHALLENGE },
    { title: 'PROMOTION', value: AdminType.PROMOTION },
  ];

  const fetchUserList = async () => {
    try {
      const result = await apiCall(userService.getUserDetails, `${id}`);
      setUserData(result?.data?.data);
    } catch (error) {
      LoggerService.log('Failed to retrieve user details', error);
    }
  };

  const fetchParticipationHistory = async () => {
    try {
      let participant = await apiCall(ParticipantService.getParticipantHistory, { loginId: id });
      setParticipationHistory(participant?.data?.data);
    } catch (error) {
      LoggerService.log('Error fetchParticipationHistory', error);
    }
  };

  const onView = (payload: DynamicObjectInterface) => {
    window.open(`/certificate?campaignId=${payload.campaignId}&teamId=${payload.teamId}&code=${payload.code}`);
  };

  const onDownload = async (payload: DynamicObjectInterface) => {
    try {
      const response = await apiCall(ParticipantService.downloadCertificate, payload);

      if (response.status === 200 && response.data) {
        // Ensure data exists
        const data = response.data;
        const blob = new Blob([data], { type: 'image/svg' }); // Correct MIME type

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `certificate-${payload.code}.png`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Release the object URL resources after the download is triggered
        URL.revokeObjectURL(link.href);
      } else {
        adminToast({
          message: response?.data?.data?.message || 'An unexpected error occurred while downloading the certificate.',
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      //console.error('Error during certificate download:', error);
      adminToast({
        message: 'Failed to download the certificate. Please try again.',
        theme: Theme.ERROR,
      });
    }
  };

  const onShare = (payload: DynamicObjectInterface) => {
    setSelectedData(payload);
  };

  const validateInputs = () => {
    if (isNewUser) {
      let isMemberEmpty = false;
      // For new user
      const { loginId, name, email, gender, frameworkRole, memberType } = userData ?? {};
      const isAnyFieldEmpty = !loginId || !name || !email || !gender || !frameworkRole;
      if (frameworkRole === FrameworkRole.MEMBER) {
        isMemberEmpty = !memberType;
      }

      setInvalid( !isAdmin ? isAnyFieldEmpty || !isValidEmail(email) || !phoneClicked || isMemberEmpty : false);
    } else {
      // For existing user
      const { name, email, adminType } = userData ?? {};
      const isAnyFieldEmpty = !name || !email;
      setInvalid( !isAdmin ? isAnyFieldEmpty || !isValidEmail(email) || !phoneClicked : !adminType);
    }

  };

  const handleInputChange = (name: string, value: DynamicObjectInterface) => {
    setUserData(prevUserData => ({
      ...prevUserData!,
      [name]: value,
    }));

    setClickedFields(prevClickedFields => ({
      ...prevClickedFields,
      [name]: value === '',
    }));

    if (name === 'email') {
      setEmailValid(isValidEmail(value as string));
    }

    if (name === 'frameworkRole' && value === FrameworkRole.ADMIN) {
      // If the frameworkRole is ADMIN, set memberType to INDIVIDUAL
      setUserData(prevUserData => ({
        ...prevUserData!,
        memberType: MemberType.INDIVIDUAL,
      }));
    }

    validateInputs();
  };

  const onSave = async () => {
    // Assuming you have an API endpoint for creating or updating a user

    // Assuming userData is the payload to send to the server
    const payload = {
      loginId: userData?.loginId,
      name: userData?.name,
      email: userData?.email,
      phone: userData?.phone,
      frameworkRole: userData?.frameworkRole,
      memberType: userData?.memberType,
      adminType: userData?.adminType,
      isPrimary: userData?.isPrimary,
      gender: userData?.gender,
      status: userData?.status,
      address: userData?.address,
      state: userData?.state,
      race: userData?.race,
      occupation: userData?.occupation,
      incomeRange: userData?.incomeRange,
    };

    // Check if phone value is not empty and dropdown selection is not empty
    try {
      if (isAdmin || (payload.phone && payload.phone.trim() !== '' && dropdownValue.trim() !== '')) {
        // Make a POST request to the API endpoint
        let result;
        if (isNewUser) {
          result = await apiCall(userService.createUser, payload);
        } else {
          result = await apiCall(userService.updateUser, `${id}`, payload);
        }

        if (isNewUser && !result?.data?.result) {
          // If creating a new user and the result is false, it's a duplicate login ID error
          adminToast({
            message: t('admin.userEdit.duplicateLoginId'),
            theme: Theme.ERROR,
          });
        } else if (!result?.data?.result) {
          // If the result status is false, show an error message
          adminToast({
            message: t('admin.userEdit.errorSaveUser'),
            theme: Theme.ERROR,
          });
        } else {
          // If the result is true or it's an existing user, it's a success
          navigate(`/admin/master/user-setup/list`);
          adminToast({
            message: isNewUser ? t('admin.userEdit.createUserSuccess') : t('admin.userEdit.updateUserSuccess'),
            theme: Theme.SUCCESS,
          });
        }
      } else {
        // Display an error message because the phone number or dropdown selection is missing
        adminToast({
          message: t('admin.userSetup.requiredPhoneAndDropdown'),
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      LoggerService.log('Error saving user', error);
      adminToast({
        message: t('admin.userEdit.errorSaveUser'),
        theme: Theme.ERROR,
      });
    }
  };

  const buttonConfig = [
    {
      title: t('admin.userEdit.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/master/user-setup/list`);
      },
    },
    {
      title: t('admin.userEdit.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: invalid,
      onClick: () => {
        if (formatCampaignId(userData?.loginId ?? '')) {
          onSave();
        } else {
          adminToast({
            message: 'Only letters (A-z) and numbers with no spaces are allowed to be set in Login ID',
            theme: Theme.ERROR,
          });
        }
      },
    },
  ];

  useEffect(() => {
    if (!isNewUser) {
      fetchUserList();
    }
  }, [id, isNewUser]);

  useEffect(() => {
    if (userData) {
      const { phone } = userData;
      if (phone) {
        const [countryCode, number] = phone.split(' ');
        setDropdownValue(countryCode);
        setPhoneValue(number);  

        if (REACT_APP_COUNTRY === 'ID') {
          setPhoneClicked(!(number.length > 0 && (number.length < 8 || number.length > 12)));
        } else {
          setPhoneClicked(!(number.length > 0 && (number.length < 8 || number.length > 10)));
        }
      }
      setGender(userData.gender);
      setStatus(userData.status);
      setIsAdmin(userData.frameworkRole === FrameworkRole.ADMIN);

      if (userData.frameworkRole === FrameworkRole.MEMBER && !isNewUser) fetchParticipationHistory();
    }
  }, [userData]);

  useEffect(() => {
    validateInputs();
  }, [userData]);

  const shareCertificateModal = (
    <>
      {selectedData?.code && (
        <ShareDrawer
          open={!!selectedData?.code}
          onClose={() =>
            setSelectedData({
              campaignId: '',
              teamId: '',
              code: '',
            })
          }
          title="Share Via"
          channels={process.env.REACT_APP_COUNTRY === 'PH' ? ['FaWhatsapp', 'FaViber', 'FaEnvelope','FaLink'] : ['FaWhatsapp','FaEnvelope','FaLink']}
          programUrl={`${window.location.origin}/certificate?campaignId=${selectedData.campaignId}&teamId=${selectedData.teamId}&code=${selectedData.code}`}
          programTitle=""
        />
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader
        title={isNewUser ? t('admin.userEdit.newTitle') : t('admin.userEdit.editTitle')}
        button={buttonConfig}
      />
      {isAdmin ? (
        <div className={styles['edit-user-form-container']}>
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={!isNewUser}
            type={InputTypes.NUMBER}
            name={'LoginID'}
            placeholder={t('admin.userSetup.loginId')}
            label={`${t('admin.userSetup.loginId')}*`}
            value={userData?.loginId}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('loginId', Number(e.target.value));
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({ ...prevClickedFields, loginId: userData?.loginId === '' }))
            }
            status={clickedFields.loginId ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={clickedFields.loginId ? t('admin.userSetup.requiredLoginId') : ''}
          />
        </div>
        {!isNewUser ? (
          <div className={styles['input-container']}>
            <div className={styles['label-text']}>{t('admin.userSetup.status')}</div>
            <div className={styles['radio-button-container']}>
              <RadioButton
                label={t('admin.userEdit.active')}
                checked={status === Status.ACTIVE}
                onChange={() => {
                  setStatus(Status.ACTIVE);
                  handleInputChange('status', Status.ACTIVE);
                }}
              />
              <RadioButton
                label={t('admin.userEdit.inactive')}
                checked={status === Status.INACTIVE}
                onChange={() => {
                  setStatus(Status.INACTIVE);
                  handleInputChange('status', Status.INACTIVE);
                }}
              />
            </div>
          </div>
        ) : null}
        <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'name'}
            placeholder="John Doe"
            label={`${t('admin.userSetup.name')}*`}
            value={userData?.name}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('name', e.target.value);
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({ ...prevClickedFields, name: userData?.name === '' }))
            }
            status={clickedFields.name ? InputValidations.INVALID : InputValidations.DEFAULT}
            // message={clickedFields.name ? t('admin.userSetup.requiredName') : ''}
            //message={'It is advisable not to edit the name as it needs to match the MDMS name'}
          />
        </div>
        <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.EMAIL}
            name={'email'}
            placeholder="john_doe@domainname.com"
            label={`${t('admin.userSetup.email')}*`}
            value={userData?.email}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('email', e.target.value);
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({ ...prevClickedFields, email: userData?.email === '' }))
            }
            status={userData?.email === '' || !emailValid ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={
              userData?.email === ''
                ? t('admin.userSetup.requiredEmail')
                : emailValid
                  ? ''
                  : t('admin.userSetup.invalidEmailFormat')
            }
          />
        </div>

        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={!isNewUser}
            type={InputTypes.TEXT}
            name={'lastLogin'}
            placeholder="-"
            label={`${t('admin.userSetup.lastLogin')}*`}
            value={userData?.lastLogin}
            mlpVersion={MLPVersion.ONE}
          />
        </div>
      </div>
      ) : (
        <div className={styles['edit-user-form-container']}>
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={!isNewUser}
            type={InputTypes.NUMBER}
            name={'LoginID'}
            placeholder={t('admin.userSetup.loginId')}
            label={`${t('admin.userSetup.loginId')}*`}
            value={userData?.loginId}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('loginId', Number(e.target.value));
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({ ...prevClickedFields, loginId: userData?.loginId === '' }))
            }
            status={clickedFields.loginId ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={clickedFields.loginId ? t('admin.userSetup.requiredLoginId') : ''}
          />
        </div>
        {!isNewUser ? (
          <div className={styles['input-container']}>
            <div className={styles['label-text']}>{t('admin.userSetup.status')}</div>
            <div className={styles['radio-button-container']}>
              <RadioButton
                label={t('admin.userEdit.active')}
                checked={status === Status.ACTIVE}
                onChange={() => {
                  setStatus(Status.ACTIVE);
                  handleInputChange('status', Status.ACTIVE);
                }}
              />
              <RadioButton
                label={t('admin.userEdit.inactive')}
                checked={status === Status.INACTIVE}
                onChange={() => {
                  setStatus(Status.INACTIVE);
                  handleInputChange('status', Status.INACTIVE);
                }}
              />
            </div>
          </div>
        ) : null}

        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={!isNewUser}
            type={InputTypes.NUMBER}
            name={'LoginID'}
            placeholder={t('admin.userSetup.partyId')}
            label={`${t('admin.userSetup.partyId')}*`}
            value={userData?.partyId}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('partyId', Number(e.target.value));
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({ ...prevClickedFields, partyId: userData?.partyId === '' }))
            }
            status={clickedFields.partyId ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={clickedFields.partyId ? t('admin.userSetup.requiredLoginId') : ''}
          />
        </div>

        <div className={styles['input-container']}>
          <div className={styles['label-text']}>{`${t('admin.userSetup.consented')}*`}</div>
          <div className={styles['radio-button-container']}>
            <RadioButton
              label={t('admin.participant.yes')}
              //disabled={true}
              checked={userData?.isAcknowledged === 1}
            />
            <RadioButton
              label={t('admin.participant.no')}
              //disabled={true}
              checked={userData?.isAcknowledged === 0 || userData?.isAcknowledged === null}
            />
          </div>
        </div>
        <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'name'}
            placeholder="John Doe"
            label={`${t('admin.userSetup.name')}*`}
            value={userData?.name}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('name', e.target.value);
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({ ...prevClickedFields, name: userData?.name === '' }))
            }
            status={clickedFields.name ? InputValidations.INVALID : InputValidations.DEFAULT}
            // message={clickedFields.name ? t('admin.userSetup.requiredName') : ''}
            message={'It is advisable not to edit the name as it needs to match the MDMS name'}
          />
        </div>
        <div className={styles['input-container']}>
          <div className={styles['label-text']}>{t('admin.userSetup.gender')}</div>
          <div className={styles['radio-button-container']}>
            <RadioButton
              label={t('admin.userEdit.male')}
              checked={gender === Gender.MALE}
              onChange={() => {
                setGender(Gender.MALE);
                handleInputChange('gender', Gender.MALE);
              }}
            />
            <RadioButton
              label={t('admin.userEdit.female')}
              checked={gender === Gender.FEMALE}
              onChange={() => {
                setGender(Gender.FEMALE);
                handleInputChange('gender', Gender.FEMALE);
              }}
            />
          </div>
          {userData?.gender === '' ? (
            <div className={styles['text-error-message']}>{t('admin.userSetup.requiredGender')}</div>
          ) : null}
        </div>
        <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.EMAIL}
            name={'email'}
            placeholder="john_doe@domainname.com"
            label={`${t('admin.userSetup.email')}*`}
            value={userData?.email}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('email', e.target.value);
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({ ...prevClickedFields, email: userData?.email === '' }))
            }
            status={userData?.email === '' || !emailValid ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={
              userData?.email === ''
                ? t('admin.userSetup.requiredEmail')
                : emailValid
                  ? ''
                  : t('admin.userSetup.invalidEmailFormat')
            }
          />
        </div>

        <div className={styles['input-container']}>
          <div className={styles['label-text-phone']}>{t('admin.userSetup.phone')}</div>

          <PhoneNumber
            name={'phoneNumber'}
            data={countryCodeNumber}
            inputValue={phoneValue}
            dropdownValue={dropdownValue}
            placeholder="Phone Number"
            status={ !isAdmin || phoneClicked ? InputValidations.DEFAULT : InputValidations.INVALID}
            message={
              phoneValue.length === 0 && !isAdmin 
                ? t('admin.userSetup.requiredPhone')
                : !phoneClicked && !isAdmin
                  ? t('admin.userSetup.invalidPhoneNumber')
                  : ''
            }
            configData={{
              label: 'name',
              onSelect: (e: DropDownData) => {
                setDropdownValue(e.name as string);
                // Check if selectedOption is not empty before setting phoneValue
                if (e.name) {
                  let _phoneValue = phoneValue;

                  // Remove leading 0 if country code exists
                  if (phoneValue?.startsWith('0')) {
                    _phoneValue = phoneValue.slice(1);
                  }

                  const combinedValue = `${e.name} ${_phoneValue}`;
                  setPhoneValue(_phoneValue === '' ? '' : _phoneValue); // Ensure phoneValue is not set if it's already empty
                  handleInputChange('phone', combinedValue);
                } else {
                  setPhoneValue(''); // Reset phoneValue to empty if dropdownValue is not selected
                  handleInputChange('phone', ''); // Update the state accordingly
                }
              },
            }}
            onInputChange={val => {
              let phoneNumber = val ?? '';

              // Remove leading 0 if country code exists
              if (!!dropdownValue && phoneNumber?.startsWith('0')) {
                phoneNumber = phoneNumber.slice(1);
              }

              setPhoneValue(phoneNumber);

              if (phoneNumber) {
                const combinedValue = `${dropdownValue} ${phoneNumber}`;
                setPhoneValue(phoneNumber === '' ? '' : phoneNumber);
                handleInputChange('phone', combinedValue);

                if (REACT_APP_COUNTRY === 'ID') {
                  setPhoneClicked(!(phoneNumber.length > 0 && (phoneNumber.length < 8 || phoneNumber.length > 12)));
                } else {
                  setPhoneClicked(!(phoneNumber.length > 0 && (phoneNumber.length < 8 || phoneNumber.length > 10)));
                }
              } else {
                setPhoneValue(''); // Reset phoneValue to empty if dropdownValue is not selected
                handleInputChange('phone', ''); // Update the state accordingly
              }
            }}
            //onClick={() => setPhoneClicked(phoneValue.length > 0)}
            maxLength={REACT_APP_COUNTRY === 'ID' ? 12 : 10}
          />
        </div>

        <div className={styles['input-section']}>
          <Input
            key="id"
            type={InputTypes.TEXT}
            name={'address'}
            placeholder=""
            label={`${t('admin.profile.address')}`}
            value={userData?.address}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('address', e.target.value);
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({ ...prevClickedFields, address: userData?.address === '' }))
            }
            status={clickedFields.address ? InputValidations.INVALID : InputValidations.DEFAULT}
          />
        </div>

        {/* Hide on country ID */}
        {REACT_APP_COUNTRY === 'MY' && (
          <DropDown
            name="state"
            dropdownLabel={`${t('admin.profile.state')}`}
            disabledFilter
            //status={userData?.state ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
            mlpVersion={MLPVersion.ONE}
            isWithinModal={false}
            value={userData?.state || ''}
            data={states}
            configData={{
              label: 'title',
              onSelect: (selectedOption: DropDownData) => {
                handleInputChange('state', selectedOption.value);
              },
            }}
          />
        )}

        {/* Hide on country ID */}
        {REACT_APP_COUNTRY === 'MY' && (
          <div className={styles['input-section']}>
            <DropDown
              name="race"
              dropdownLabel={`${t('admin.profile.race')}`}
              disabledFilter
              //status={userData?.race ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
              mlpVersion={MLPVersion.ONE}
              value={
                userData?.race ? userData?.race?.charAt(0).toUpperCase() + userData?.race?.slice(1).toLowerCase() : ''
              }
              data={[
                { value: 'CHINESE', title: 'Chinese' },
                { value: 'MALAY', title: 'Malay' },
                { value: 'INDIAN', title: 'Indian' },
                { value: 'OTHER', title: 'Other' },
              ]}
              configData={{
                label: 'title',
                onSelect: (selectedOption: DropDownData) => {
                  handleInputChange('race', selectedOption.value);
                },
              }}
            />
          </div>
        )}

        {/* Hide on country ID */}
        {REACT_APP_COUNTRY === 'MY' && (
          <div className={styles['input-section']}>
            <DropDown
              name="occupation"
              dropdownLabel={`${t('admin.profile.occupation')}`}
              disabledFilter
              //status={userData?.occupation ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
              mlpVersion={MLPVersion.ONE}
              isWithinModal={false}
              value={userData?.occupation || ''}
              data={[
                { value: 'Full Time ABO', title: 'Full Time ABO' },
                { value: 'Business and Sales', title: 'Business and Sales' },
                { value: 'Education and Training', title: 'Education and Training' },
                { value: 'Healthcare', title: 'Healthcare' },
                { value: 'Engineering and IT', title: 'Engineering and IT' },
                { value: 'Legal and Finance', title: 'Legal and Finance' },
                { value: 'Marketing and Customer Service', title: 'Marketing and Customer Service' },
                { value: 'Administration and Support', title: 'Administration and Support' },
                { value: 'Other', title: 'Other' },
              ]}
              configData={{
                label: 'title',
                onSelect: (selectedOption: DropDownData) => {
                  handleInputChange('occupation', selectedOption.value);
                },
              }}
            />
          </div>
        )}

        {/* Hide on country ID */}
        {REACT_APP_COUNTRY === 'MY' && (
          <div className={styles['input-section']}>
            <DropDown
              name="incomeRange"
              dropdownLabel={`${t('admin.profile.incomeRange')}`}
              disabledFilter
              //status={userData?.incomeRange ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
              mlpVersion={MLPVersion.ONE}
              value={userData?.incomeRange || ''}
              data={[
                { value: 'Below RM3,500', title: 'Below RM3,500' },
                { value: 'RM3,501 - RM8,000', title: 'RM3,501 - RM8,000' },
                { value: 'RM8,001 - RM11,000', title: 'RM8,001 - RM11,000' },
                { value: 'Above RM11,000', title: 'Above RM11,000' },
              ]}
              configData={{
                label: 'title',
                onSelect: (selectedOption: DropDownData) => {
                  handleInputChange('incomeRange', selectedOption.value);
                },
              }}
            />
          </div>
        )}
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={!isNewUser}
            type={InputTypes.TEXT}
            name={'lastLogin'}
            placeholder="-"
            label={`${t('admin.userSetup.lastLogin')}*`}
            value={userData?.lastLogin}
            mlpVersion={MLPVersion.ONE}
          />
        </div>
      </div>

      )}
      
      <hr className={styles['horizontal-line']} />
      <div className={styles['edit-user-form-container']}>
        <div className={styles['input-container']}>
          <DropDown
            name="frameworkRole"
            dropdownLabel={`${t('admin.userSetup.frameworkRole')}*`}
            disabled={true}
            disabledFilter
            status={clickedFields.frameworkRole ? DropDownValidations.INVALID : DropDownValidations.DEFAULT}
            message={clickedFields.frameworkRole ? t('admin.userSetup.requiredRole') : ''}
            mlpVersion={MLPVersion.ONE}
            value={userData?.frameworkRole}
            data={[
              { value: FrameworkRole.MEMBER, title: FrameworkRole.MEMBER },
              { value: FrameworkRole.ADMIN, title: FrameworkRole.ADMIN },
            ]}
            configData={{
              label: 'title',
              onSelect: (selectedOption: DropDownData) => {
                handleInputChange('frameworkRole', selectedOption.value);
              },
            }}
          />
        </div>
        {isAdmin && ( 
        <div className={styles['input-container']}>
          <DropDown
            id={'adminType'}
            key={'adminType'}
            name={'adminType'}
            //top={'-100px'}
            dropdownLabel={t('admin.userSetup.adminType')}
            placeholder={t('admin.userSetup.adminType')}
            mlpVersion={MLPVersion.ONE}
            //multiSelection
            //allSelectionOption
            data={adminTypeOptions}
            value={userData?.adminType ? adminTypeOptions.find(option => option.value === userData.adminType)?.title : ''}
            configData={{
              label: 'title',
              onSelect: (selectedOption: DropDownData) => {
                handleInputChange('adminType', selectedOption.value);
              },
            }}
          />
        </div>
        )}
        <div/>
      </div>
      <br />
      <br />

      {!isAdmin && !isNewUser && (
        <>
          <Accordion
            id="participationHistoryAccordion"
            key="participationHistoryAccordion"
            title="Participation History"
            iconType={ControlIconType.CHEVRON}
            fontSize={FontSize.MEDIUM}
          >
            {participationHistory?.length > 0 ? (
              <TableContainer
                dataSource={participationHistory}
                getColumns={getParticipationHistoryConfig}
                isAdmin
                rowKey="recId"
                cb={{ onView, onDownload, onShare }}
                displayOverflow={true}
                pagination={false}
              />
            ) : (
              <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
            )}
          </Accordion>
          {shareCertificateModal}
        </>
      )}
    </div>
  );
};
